/**
 * Main Layout with GoldenLayout
 *
 * Context-aware dynamic layout that changes tabs based on selected artifact
 */

import { useEffect, useRef, useCallback, useMemo } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { GoldenLayout, LayoutConfig, ComponentContainer } from 'golden-layout';
import { ExplorerPanel } from '../panels/ExplorerPanel';
import { AgentPromptPanel } from '../panels/AgentPromptPanel';
import { ArtifactPromptPanel } from '../panels/ArtifactPromptPanel';
import { AIHistoryPanel } from '../panels/AIHistoryPanel';
import { AuditLogPanel } from '../panels/AuditLogPanel';
import { PlanEditorPanel } from '../panels/PlanEditorPanel';
import { LumaScriptEditorPanel } from '../panels/LumaScriptEditorPanel';
import { BPMNCanvasPanel } from '../panels/BPMNCanvasPanel';
import { LumaScriptPreviewPanel } from '../panels/LumaScriptPreviewPanel';
import { ArtifactDesignPanel } from '../panels/ArtifactDesignPanel';
import { LumaJSONViewerPanel } from '../panels/LumaJSONViewerPanel';
import { BPMNEditorPanel } from '../panels/BPMNEditorPanel';
import { LumaJsonPanel } from '../panels/LumaJSONPanel';
import { AIAssistantPanel } from '../panels/AIAssistantPanel';
import { ValidationPanel } from '../panels/ValidationPanel';
import { AttributeEditorPanel } from '../panels/AttributeEditorPanel';
import { IntegrationEditorPanel } from '../panels/IntegrationEditorPanel';
import { TopBar } from './TopBar';
import { StatusBar } from './StatusBar';
import { ToastContainer } from '../ui/ToastContainer';
import { useProjectStore, type StageType, type ArtifactType } from '@/stores/projectStore';
/* GoldenLayout CSS is imported in index.css for proper load order */

/**
 * Generate GoldenLayout config based on context
 *
 * Context determines which tabs appear:
 * - Project context (no artifact): Agent Plan, LumaScript, BPMN Preview, Artifact Design
 * - Skill/Subskill/Subflow context: Prompt, Artifact Plan, LumaScript, BPMN Preview, LumaJSON
 * - Attribute context: Attribute Editor (single tab)
 * - Integration context: Integration Editor (single tab)
 */
function generateLayoutConfig(selectedArtifactId: string | null, artifactType: ArtifactType | null): LayoutConfig {
  // Determine which tabs to show in the center editor stack
  let editorTabs: Array<{ type: string; componentType: string; title: string; isClosable: boolean }> = [];

  if (!selectedArtifactId) {
    // PROJECT CONTEXT: Master Pipeline tabs
    editorTabs = [
      { type: 'component', componentType: 'agentPrompt', title: 'Skills Agent', isClosable: false },
      { type: 'component', componentType: 'plan', title: 'Agent Plan', isClosable: false },
      { type: 'component', componentType: 'lumascript', title: 'LumaScript', isClosable: false },
      { type: 'component', componentType: 'lumascriptPreview', title: 'LumaScript Preview', isClosable: false },
      { type: 'component', componentType: 'artifactDesign', title: 'Artifact Generation', isClosable: false }
    ];
  } else {
    // ARTIFACT CONTEXT: Different tabs based on artifact type
    switch (artifactType) {
      case 'SKILL':
        // Skill-specific pipeline
        editorTabs = [
          { type: 'component', componentType: 'prompt', title: 'Skill Prompt', isClosable: false },
          { type: 'component', componentType: 'plan', title: 'Skill Plan', isClosable: false },
          { type: 'component', componentType: 'lumascript', title: 'LumaScript', isClosable: false },
          { type: 'component', componentType: 'lumascriptPreview', title: 'LumaScript Preview', isClosable: false },
          { type: 'component', componentType: 'bpmnEditor', title: 'BPMN Editor', isClosable: false },
          { type: 'component', componentType: 'lumajsonViewer', title: 'LumaJSON Viewer', isClosable: false },
          { type: 'component', componentType: 'lumaJson', title: 'LumaJSON', isClosable: false }
        ];
        break;

      case 'SUBSKILL':
        // Subskill-specific pipeline
        editorTabs = [
          { type: 'component', componentType: 'prompt', title: 'Subskill Prompt', isClosable: false },
          { type: 'component', componentType: 'plan', title: 'Subskill Plan', isClosable: false },
          { type: 'component', componentType: 'lumascript', title: 'LumaScript', isClosable: false },
          { type: 'component', componentType: 'lumascriptPreview', title: 'LumaScript Preview', isClosable: false },
          { type: 'component', componentType: 'bpmnEditor', title: 'BPMN Editor', isClosable: false },
          { type: 'component', componentType: 'lumajsonViewer', title: 'LumaJSON Viewer', isClosable: false },
          { type: 'component', componentType: 'lumaJson', title: 'LumaJSON', isClosable: false }
        ];
        break;

      case 'SUBFLOW':
        // Subflow-specific pipeline
        editorTabs = [
          { type: 'component', componentType: 'prompt', title: 'Subflow Prompt', isClosable: false },
          { type: 'component', componentType: 'plan', title: 'Subflow Plan', isClosable: false },
          { type: 'component', componentType: 'lumascript', title: 'LumaScript', isClosable: false },
          { type: 'component', componentType: 'lumascriptPreview', title: 'LumaScript Preview', isClosable: false },
          { type: 'component', componentType: 'bpmnEditor', title: 'BPMN Editor', isClosable: false },
          { type: 'component', componentType: 'lumajsonViewer', title: 'LumaJSON Viewer', isClosable: false },
          { type: 'component', componentType: 'lumaJson', title: 'LumaJSON', isClosable: false }
        ];
        break;

      case 'ATTRIBUTE':
        // Attribute editor (custom form)
        editorTabs = [
          { type: 'component', componentType: 'prompt', title: 'Attribute Prompt', isClosable: false },
          { type: 'component', componentType: 'plan', title: 'Attribute Plan', isClosable: false },
          { type: 'component', componentType: 'lumaJson', title: 'LumaJSON', isClosable: false },
          { type: 'component', componentType: 'attributeEditor', title: 'Attribute Editor', isClosable: false }
        ];
        break;

      case 'INTEGRATION':
        // Integration editor (custom form)
        editorTabs = [
          { type: 'component', componentType: 'prompt', title: 'Integration Prompt', isClosable: false },
          { type: 'component', componentType: 'plan', title: 'Integration Plan', isClosable: false },
          { type: 'component', componentType: 'lumaJson', title: 'LumaJSON', isClosable: false },
          { type: 'component', componentType: 'integrationEditor', title: 'Integration Editor', isClosable: false }
        ];
        break;

      case 'FACET':
        // FACET: Coming soon - placeholder only
        editorTabs = [
          { type: 'component', componentType: 'facetPlaceholder', title: 'Facets - Coming Soon', isClosable: false }
        ];
        break;

      default:
        // Fallback: treat as skill
        editorTabs = [
          { type: 'component', componentType: 'prompt', title: 'Prompt', isClosable: false },
          { type: 'component', componentType: 'plan', title: 'Artifact Plan', isClosable: false },
          { type: 'component', componentType: 'lumascript', title: 'LumaScript', isClosable: false },
          { type: 'component', componentType: 'lumascriptPreview', title: 'LumaScript Preview', isClosable: false },
          { type: 'component', componentType: 'bpmnEditor', title: 'BPMN Editor', isClosable: false },
          { type: 'component', componentType: 'lumajsonViewer', title: 'LumaJSON Viewer', isClosable: false },
          { type: 'component', componentType: 'lumaJson', title: 'LumaJSON', isClosable: false }
        ];
    }
  }

  const layoutContent: any[] = [
    {
      type: 'stack',
      width: 25.5,
      header: {
        show: 'top',
        popout: false,
        maximise: 'false',
        close: 'false'
      },
      content: [
        {
          type: 'component',
          componentType: 'explorer',
          title: 'Explorer',
          isClosable: false
        }
      ]
    },
    {
      type: 'stack',
      header: {
        show: 'top',
        popout: false,
        maximise: 'false',
        close: 'false'
      },
      content: editorTabs
    }
  ];

  return {
    root: {
      type: 'row',
      content: layoutContent
    }
  };
}

/**
 * Maps a component type to its semantic equivalent in a different artifact type context.
 * This enables tab context preservation when switching between artifact types.
 *
 * Examples:
 * - skillEditor → attributeEditor (when switching from SKILL to ATTRIBUTE)
 * - attributeEditor → integrationEditor (when switching from ATTRIBUTE to INTEGRATION)
 * - lumaJson → lumaJson (universal across all types)
 */
function mapComponentTypeToArtifactType(
  currentComponentType: string,
  targetArtifactType: ArtifactType | null
): string | null {
  // Universal tabs - these exist on all artifact types
  if (['prompt', 'plan', 'lumaJson'].includes(currentComponentType)) {
    return currentComponentType;
  }

  // SKILL/SUBSKILL/SUBFLOW-specific tabs
  if (['lumascript', 'lumascriptPreview', 'bpmnEditor', 'lumajsonViewer'].includes(currentComponentType)) {
    // These only exist on workflow artifacts
    if (targetArtifactType === 'SKILL' || targetArtifactType === 'SUBSKILL' || targetArtifactType === 'SUBFLOW') {
      return currentComponentType;
    }
    // When switching to non-workflow types, fall back to editor
    return mapEditorForArtifactType(targetArtifactType);
  }

  // Type-specific editors - map to the equivalent editor for target type
  if (['attributeEditor', 'integrationEditor'].includes(currentComponentType)) {
    return mapEditorForArtifactType(targetArtifactType);
  }

  // For any other tabs (project-level, etc.), return null to use default
  return null;
}

/**
 * Returns the editor component type for a given artifact type
 */
function mapEditorForArtifactType(artifactType: ArtifactType | null): string | null {
  switch (artifactType) {
    case 'SKILL':
    case 'SUBSKILL':
    case 'SUBFLOW':
      return 'bpmnEditor'; // Main visual editor for workflow artifacts
    case 'ATTRIBUTE':
      return 'attributeEditor';
    case 'INTEGRATION':
      return 'integrationEditor';
    case 'FACET':
      return 'facetPlaceholder'; // FACET doesn't have a real editor yet
    default:
      return null;
  }
}

export const MainLayout: React.FC = () => {
  const layoutContainerRef = useRef<HTMLDivElement>(null);
  const goldenLayoutRef = useRef<GoldenLayout | null>(null);
  const rootsRef = useRef<Map<string, Root>>(new Map());
  const lastContextRef = useRef<{ selectedArtifactId: string | null; artifactType: string | null }>({ selectedArtifactId: null, artifactType: null });
  const lastLayoutModeRef = useRef<string | null>(null); // Track which layout configuration is active
  const lastActiveTabRef = useRef<string | null>(null); // Track the last active tab componentType for context preservation

  const activeStage = useProjectStore((state) => state.ui.activeStage);
  const selectedArtifactId = useProjectStore((state) => state.ui.selectedArtifactId);
  const activeProjectId = useProjectStore((state) => state.activeProjectId);

  // Get artifact type directly from store with stable reference
  const artifactType = useProjectStore(
    useCallback((state) => {
      if (!state.ui.selectedArtifactId || !state.activeProjectId) {
        return null;
      }
      const project = state.projects[state.activeProjectId];
      if (!project) return null;
      const artifact = project.artifacts[state.ui.selectedArtifactId];
      return artifact?.type || null;
    }, [])
  );

  // Component registration function (called once per layout instance)
  const registerComponents = useCallback((layout: GoldenLayout) => {
    // Explorer
    layout.registerComponentFactoryFunction('explorer', (container: ComponentContainer) => {
      const div = document.createElement('div');
      div.style.width = '100%';
      div.style.height = '100%';
      container.element.appendChild(div);

      const root = createRoot(div);
      root.render(<ExplorerPanel container={container} />);
      rootsRef.current.set(container.element.id, root);

      return {
        destroy: () => {
          root.unmount();
          rootsRef.current.delete(container.element.id);
        }
      };
    });

    // Agent Prompt (project context only)
    layout.registerComponentFactoryFunction('agentPrompt', (container: ComponentContainer) => {
      const div = document.createElement('div');
      div.style.width = '100%';
      div.style.height = '100%';
      container.element.appendChild(div);

      const root = createRoot(div);
      root.render(<AgentPromptPanel />);
      rootsRef.current.set(container.element.id, root);

      return {
        destroy: () => {
          root.unmount();
          rootsRef.current.delete(container.element.id);
        }
      };
    });

    // Prompt Editor (artifact context only)
    layout.registerComponentFactoryFunction('prompt', (container: ComponentContainer) => {
      const div = document.createElement('div');
      div.style.width = '100%';
      div.style.height = '100%';
      container.element.appendChild(div);

      const root = createRoot(div);
      root.render(<ArtifactPromptPanel />);
      rootsRef.current.set(container.element.id, root);

      return {
        destroy: () => {
          root.unmount();
          rootsRef.current.delete(container.element.id);
        }
      };
    });

    // Plan Editor (context-aware: project plan or artifact plan)
    layout.registerComponentFactoryFunction('plan', (container: ComponentContainer) => {
      const div = document.createElement('div');
      div.style.width = '100%';
      div.style.height = '100%';
      container.element.appendChild(div);

      const root = createRoot(div);
      root.render(<PlanEditorPanel />);
      rootsRef.current.set(container.element.id, root);

      return {
        destroy: () => {
          root.unmount();
          rootsRef.current.delete(container.element.id);
        }
      };
    });

    // LumaScript Editor (context-aware)
    layout.registerComponentFactoryFunction('lumascript', (container: ComponentContainer) => {
      const div = document.createElement('div');
      div.style.width = '100%';
      div.style.height = '100%';
      container.element.appendChild(div);

      const root = createRoot(div);
      root.render(<LumaScriptEditorPanel />);
      rootsRef.current.set(container.element.id, root);

      return {
        destroy: () => {
          root.unmount();
          rootsRef.current.delete(container.element.id);
        }
      };
    });

    // BPMN Canvas (context-aware)
    layout.registerComponentFactoryFunction('bpmn', (container: ComponentContainer) => {
      const div = document.createElement('div');
      div.style.width = '100%';
      div.style.height = '100%';
      container.element.appendChild(div);

      const root = createRoot(div);
      root.render(<BPMNCanvasPanel />);
      rootsRef.current.set(container.element.id, root);

      return {
        destroy: () => {
          root.unmount();
          rootsRef.current.delete(container.element.id);
        }
      };
    });

    // LumaScript Preview Panel (project context only)
    layout.registerComponentFactoryFunction('lumascriptPreview', (container: ComponentContainer) => {
      const div = document.createElement('div');
      div.style.width = '100%';
      div.style.height = '100%';
      container.element.appendChild(div);

      const root = createRoot(div);
      root.render(<LumaScriptPreviewPanel />);
      rootsRef.current.set(container.element.id, root);

      return {
        destroy: () => {
          root.unmount();
          rootsRef.current.delete(container.element.id);
        }
      };
    });

    // Artifact Design Panel (project context only)
    layout.registerComponentFactoryFunction('artifactDesign', (container: ComponentContainer) => {
      const div = document.createElement('div');
      div.style.width = '100%';
      div.style.height = '100%';
      container.element.appendChild(div);

      const root = createRoot(div);
      root.render(<ArtifactDesignPanel />);
      rootsRef.current.set(container.element.id, root);

      return {
        destroy: () => {
          root.unmount();
          rootsRef.current.delete(container.element.id);
        }
      };
    });

    // BPMN Editor - Visual BPMN editor using bpmn-js (artifact context only)
    layout.registerComponentFactoryFunction('bpmnEditor', (container: ComponentContainer) => {
      const div = document.createElement('div');
      div.style.width = '100%';
      div.style.height = '100%';
      container.element.appendChild(div);

      const root = createRoot(div);
      root.render(<BPMNEditorPanel />);
      rootsRef.current.set(container.element.id, root);

      return {
        destroy: () => {
          root.unmount();
          rootsRef.current.delete(container.element.id);
        }
      };
    });

    // LumaJSON Viewer - Read-only LumaJSON visualization (artifact context only)
    layout.registerComponentFactoryFunction('lumajsonViewer', (container: ComponentContainer) => {
      const div = document.createElement('div');
      div.style.width = '100%';
      div.style.height = '100%';
      container.element.appendChild(div);

      const root = createRoot(div);
      root.render(<LumaJSONViewerPanel />);
      rootsRef.current.set(container.element.id, root);

      return {
        destroy: () => {
          root.unmount();
          rootsRef.current.delete(container.element.id);
        }
      };
    });

    // LumaJSON - Raw JSON viewer (artifact context only)
    layout.registerComponentFactoryFunction('lumaJson', (container: ComponentContainer) => {
      const div = document.createElement('div');
      div.style.width = '100%';
      div.style.height = '100%';
      container.element.appendChild(div);

      const root = createRoot(div);
      root.render(<LumaJsonPanel />);
      rootsRef.current.set(container.element.id, root);

      return {
        destroy: () => {
          root.unmount();
          rootsRef.current.delete(container.element.id);
        }
      };
    });

    // Attribute Editor (attribute context only)
    layout.registerComponentFactoryFunction('attributeEditor', (container: ComponentContainer) => {
      const div = document.createElement('div');
      div.style.width = '100%';
      div.style.height = '100%';
      container.element.appendChild(div);

      const root = createRoot(div);
      root.render(<AttributeEditorPanel />);
      rootsRef.current.set(container.element.id, root);

      return {
        destroy: () => {
          root.unmount();
          rootsRef.current.delete(container.element.id);
        }
      };
    });

    // Integration Editor (integration context only)
    layout.registerComponentFactoryFunction('integrationEditor', (container: ComponentContainer) => {
      const div = document.createElement('div');
      div.style.width = '100%';
      div.style.height = '100%';
      container.element.appendChild(div);

      const root = createRoot(div);
      root.render(<IntegrationEditorPanel />);
      rootsRef.current.set(container.element.id, root);

      return {
        destroy: () => {
          root.unmount();
          rootsRef.current.delete(container.element.id);
        }
      };
    });

    // Facet Placeholder (coming soon)
    layout.registerComponentFactoryFunction('facetPlaceholder', (container: ComponentContainer) => {
      const div = document.createElement('div');
      div.style.width = '100%';
      div.style.height = '100%';
      div.style.display = 'flex';
      div.style.alignItems = 'center';
      div.style.justifyContent = 'center';
      div.style.padding = '2rem';
      container.element.appendChild(div);

      const root = createRoot(div);
      root.render(
        <div className="text-center space-y-4 max-w-2xl">
          <div className="text-6xl">🚧</div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Facets - Coming Soon
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-lg">
            Facet artifacts are currently under development. We're designing the best workflow for facets and determining whether they should be specialized subflows, subskills, or a unique artifact type.
          </p>
          <p className="text-gray-500 dark:text-gray-500 text-sm">
            Check back in a future release!
          </p>
        </div>
      );
      rootsRef.current.set(container.element.id, root);

      return {
        destroy: () => {
          root.unmount();
          rootsRef.current.delete(container.element.id);
        }
      };
    });

    // AI Assistant
    layout.registerComponentFactoryFunction('assistant', (container: ComponentContainer) => {
      const div = document.createElement('div');
      div.style.width = '100%';
      div.style.height = '100%';
      container.element.appendChild(div);

      const root = createRoot(div);
      root.render(<AIAssistantPanel container={container} />);
      rootsRef.current.set(container.element.id, root);

      return {
        destroy: () => {
          root.unmount();
          rootsRef.current.delete(container.element.id);
        }
      };
    });
  }, []);

  // Subscribe to active stage changes and switch tabs
  useEffect(() => {
    if (!goldenLayoutRef.current) return;

    const layout = goldenLayoutRef.current;
  const stageToComponentMap: Record<StageType, string> = {
    PROMPT: 'prompt',
    PLAN: 'plan',
    ARTIFACT_PLAN: 'plan', // Same component, context-aware
    LUMASCRIPT: 'lumascript',
    BPMN: 'lumascriptPreview', // Render BPMN in the LumaScript Preview tab
    ARTIFACT_DESIGN: 'artifactDesign',
    LUMA_SKILL: 'skillEditor', // Visual node editor for skills
    LUMAJSON: 'lumaJson' // Raw JSON viewer (all artifact types)
  };

    // Find the editor stack (center column with tabs)
    const findEditorStack = (item: any): any => {
      if (!item) return null; // Safety check for undefined/null
      if (item.type === 'stack') {
        const hasEditorComponents = item.contentItems?.some((contentItem: any) =>
          ['plan', 'lumascript', 'lumascriptPreview', 'artifactDesign', 'skillEditor', 'lumaJson', 'prompt', 'attributeEditor', 'integrationEditor'].includes(contentItem.componentType)
        );
        if (hasEditorComponents) return item;
      }
      if (item.contentItems) {
        for (const child of item.contentItems) {
          const found = findEditorStack(child);
          if (found) return found;
        }
      }
      return null;
    };

    const editorStack = findEditorStack(layout.rootItem);
    if (!editorStack) return;

    // Find the ComponentItem matching the active stage
    const componentType = stageToComponentMap[activeStage];
    const targetComponentItem = editorStack.contentItems?.find((item: any) =>
      item.componentType === componentType
    );

    if (targetComponentItem) {
      // ALWAYS switch to the target tab, even if we think it's already active
      // This fixes the bug where manual tab clicks desync Zustand state from GoldenLayout state
      console.log(`Switching to ${componentType} tab`);
      editorStack.setActiveComponentItem(targetComponentItem, true, false);
    }
  }, [activeStage]);

  // Main effect: Create/rebuild layout when context changes
  useEffect(() => {
    if (!layoutContainerRef.current) return;

    // Determine current layout mode (which layout configuration we need)
    // Different artifact types can share the same layout mode
    let currentLayoutMode: string;
    if (!selectedArtifactId) {
      currentLayoutMode = 'PROJECT';
    } else if (!artifactType) {
      // Artifact selected but type not loaded yet - skip this render
      return;
    } else {
      switch (artifactType) {
        case 'SKILL':
          currentLayoutMode = 'SKILL';
          break;
        case 'SUBSKILL':
          currentLayoutMode = 'SUBSKILL';
          break;
        case 'SUBFLOW':
          currentLayoutMode = 'SUBFLOW';
          break;
        case 'ATTRIBUTE':
          currentLayoutMode = 'ATTRIBUTE';
          break;
        case 'INTEGRATION':
          currentLayoutMode = 'INTEGRATION';
          break;
        case 'FACET':
          currentLayoutMode = 'FACET';
          break;
        default:
          currentLayoutMode = 'ARTIFACT_STANDARD'; // Fallback to standard artifact layout
      }
    }

    // Guard: Only rebuild if the layout MODE changed (not just artifact ID)
    // This prevents unnecessary rebuilds when switching between artifacts of the same type
    if (lastLayoutModeRef.current !== currentLayoutMode) {

      // Update mode ref BEFORE cleanup to prevent re-entry
      lastLayoutModeRef.current = currentLayoutMode;
      lastContextRef.current = { selectedArtifactId, artifactType };

      // Capture the currently active tab BEFORE destroying the old layout
      // This enables tab context preservation when switching artifact types
      let capturedActiveTab: string | null = null;
      const oldLayout = goldenLayoutRef.current;
      if (oldLayout) {
        try {
          // Find the editor stack and get the active tab
          const findEditorStack = (item: any): any => {
            if (!item) return null;
            if (item.type === 'stack') {
                  const hasEditorComponents = item.contentItems?.some((contentItem: any) =>
                    ['plan', 'lumascript', 'lumascriptPreview', 'artifactDesign', 'skillEditor', 'lumaJson', 'prompt', 'attributeEditor', 'integrationEditor', 'agentPrompt', 'agentPlan'].includes(contentItem.componentType)
                  );
              if (hasEditorComponents) return item;
            }
            if (item.contentItems) {
              for (const child of item.contentItems) {
                const found = findEditorStack(child);
                if (found) return found;
              }
            }
            return null;
          };

          const editorStack = findEditorStack(oldLayout.rootItem);
          if (editorStack) {
            // GoldenLayout v2 uses private property '_activeComponentItem'
            const activeItem = (editorStack as any)._activeComponentItem;
            if (activeItem?.componentType) {
              capturedActiveTab = activeItem.componentType;
            }
          }
        } catch (e) {
          console.warn('[MainLayout] Failed to capture active tab:', e);
        }
      }

      // Capture old roots for cleanup
      const oldRoots = oldLayout ? Array.from(rootsRef.current.values()) : [];

      // DESTROY OLD LAYOUT FIRST to ensure clean container
      if (oldLayout) {
        try {
          oldLayout.destroy();
        } catch (e) {
          console.error('[MainLayout] Error destroying old layout:', e);
        }

        // Manually clear container to remove any GoldenLayout debris
        if (layoutContainerRef.current) {
          layoutContainerRef.current.innerHTML = '';
        }

        oldRoots.forEach((root) => {
          try {
            root.unmount();
          } catch (e) {
            // Ignore unmount errors
          }
        });
        rootsRef.current.clear();
      }

      // Generate context-aware config for NEW layout
      const config = generateLayoutConfig(selectedArtifactId, artifactType);

      // Create NEW GoldenLayout instance
      const newLayout = new GoldenLayout(layoutContainerRef.current);

      // Register all components on NEW layout
      registerComponents(newLayout);

      // Load the NEW layout
      newLayout.loadLayout(config);

      // Update refs to point to NEW layout BEFORE cleaning up old one
      goldenLayoutRef.current = newLayout;
      // Note: rootsRef will be repopulated by registerComponents callbacks

      // Expose NEW layout to window immediately
      (window as any).__goldenLayout = newLayout;

      // SYNC GOLDENLAYOUT TAB CLICKS BACK TO ZUSTAND STATE
      // Listen for tab changes and update activeStage to keep state in sync
      const componentToStageMap: Record<string, StageType> = {
        'prompt': 'PROMPT',
        'plan': selectedArtifactId ? 'ARTIFACT_PLAN' : 'PLAN',
        'agentPrompt': 'PROMPT',
        'lumascript': 'LUMASCRIPT',
        'lumascriptPreview': 'BPMN', // Preview tab owns BPMN content
        'artifactDesign': 'ARTIFACT_DESIGN',
        'skillEditor': 'LUMA_SKILL',
        'attributeEditor': 'LUMA_SKILL', // Attributes use visual editor too
        'integrationEditor': 'LUMA_SKILL', // Integrations use visual editor too
        'lumaJson': 'LUMAJSON'
      };

      // Find editor stack and add listener
      const findEditorStack = (item: any): any => {
        if (!item) return null;
        if (item.type === 'stack') {
          const hasEditorComponents = item.contentItems?.some((contentItem: any) =>
            ['plan', 'lumascript', 'lumascriptPreview', 'artifactDesign', 'skillEditor', 'lumaJson', 'prompt', 'attributeEditor', 'integrationEditor', 'agentPrompt'].includes(contentItem.componentType)
          );
          if (hasEditorComponents) return item;
        }
        if (item.contentItems) {
          for (const child of item.contentItems) {
            const found = findEditorStack(child);
            if (found) return found;
          }
        }
        return null;
      };

    const editorStack = findEditorStack(newLayout.rootItem);
    if (editorStack) {
      // Listen for active tab changes
      editorStack.on('activeContentItemChanged', (item: any) => {
        const componentType = item?.componentType;
        if (componentType && componentType in componentToStageMap) {
            const mappedStage = componentToStageMap[componentType];
            const { ui } = useProjectStore.getState();
            // Only update if different to avoid unnecessary re-renders
            if (ui.activeStage !== mappedStage) {
              console.log(`[MainLayout] Syncing GoldenLayout tab change: ${componentType} -> ${mappedStage}`);
              useProjectStore.getState().setActiveStage(mappedStage);
            }
          }
        });
      }

      // TAB CONTEXT PRESERVATION: Try to activate the equivalent tab in the new layout
      if (capturedActiveTab) {
        // Map the old tab to its equivalent in the new artifact type
        let targetTab = mapComponentTypeToArtifactType(capturedActiveTab, artifactType);

        // Project context should default to Agent Prompt to avoid landing on empty downstream tabs
        if (!selectedArtifactId) {
          targetTab = 'agentPrompt';
        }

        if (targetTab) {
          // Defer tab activation slightly to ensure layout is fully initialized
          setTimeout(() => {
            try {
              const findEditorStack = (item: any): any => {
                if (!item) return null;
                if (item.type === 'stack') {
                  const hasEditorComponents = item.contentItems?.some((contentItem: any) =>
                    ['plan', 'lumascript', 'lumascriptPreview', 'artifactDesign', 'skillEditor', 'lumaJson', 'prompt', 'attributeEditor', 'integrationEditor', 'agentPrompt', 'agentPlan'].includes(contentItem.componentType)
                  );
                  if (hasEditorComponents) return item;
                }
                if (item.contentItems) {
                  for (const child of item.contentItems) {
                    const found = findEditorStack(child);
                    if (found) return found;
                  }
                }
                return null;
              };

              const editorStack = findEditorStack(newLayout.rootItem);
              if (editorStack) {
                const targetItem = editorStack.contentItems?.find((item: any) => item.componentType === targetTab);
                if (targetItem) {
                  editorStack.setActiveComponentItem(targetItem, true, false);
                  lastActiveTabRef.current = targetTab;
                } else {
                  console.log(`[MainLayout] Target tab ${targetTab} not found in new layout. Available: ${editorStack.contentItems?.map((i: any) => i.componentType).join(', ')}`);
                }
              } else {
                console.log(`[MainLayout] Editor stack not found in new layout`);
              }
            } catch (e) {
              console.warn('[MainLayout] Failed to restore tab context:', e);
            }
          }, 150); // Small delay to ensure layout is ready
        }
      }
    }
  }, [selectedArtifactId, artifactType]); // Rebuild when artifact selection changes OR when type resolves

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (goldenLayoutRef.current) {
        try {
          goldenLayoutRef.current.destroy();
        } catch (e) {
          console.error('[MainLayout] Error destroying layout on unmount:', e);
        }
      }
      
      rootsRef.current.forEach((root) => {
        try {
          root.unmount();
        } catch (e) {
          // Ignore unmount errors
        }
      });
      rootsRef.current.clear();
    };
  }, []);

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <div className="flex-shrink-0">
        <TopBar />
      </div>
      <div ref={layoutContainerRef} className="flex-1 overflow-hidden" />
      <div className="flex-shrink-0">
        <StatusBar />
      </div>
      <ToastContainer />
    </div>
  );
};
